import React from 'react';
import { userInfo, userTier } from '@apollo-internal/cache';
import { useQuery, useReactiveVar } from '@apollo/client';
import ApolloError from '@shared/components/ApolloError';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { getDataNotFoundPage } from '@shared/services/getDataNotFoundPageService';
import GET_SELECTED_ORG_FOR_EVENT from '../../../queries/getSelectedOrgForEventQuery';
import useCalendarPageMode from '../../shared/hooks/useCalendarPageMode';
import { useIsUserActingAsATMStore, useSelectedDateStore } from '../../../CalendarStore';
import useATLRoleInfo from '../../shared/hooks/useATLRoleInfo';
import EventContextProvider from '../../shared/providers/EventContextProvider';
import EditEventComponentWrapper from './EditEventComponentWrapper';
import useEditEventData from '../../../hooks/useEditEventData';
import { useShallow } from 'zustand/shallow';

const EditEventPage = () => {
  const { eventId } = useParams();
  const location = useLocation();

  const isUserActingAsATM = useIsUserActingAsATMStore(useShallow(state => state.isUserActingAsATM));
  const setIsUserActingAsATM = useIsUserActingAsATMStore.getState().setIsUserActingAsATM;
  const setSelectedDate = useSelectedDateStore(state => state.setSelectedDate);

  const eventIdParsed = parseInt(eventId);
  const currentUserInfo = useReactiveVar(userInfo);
  const currentUserTier = useReactiveVar(userTier);

  const { isUserATLActingAsATM, isUserATL } = useATLRoleInfo();

  const {
    event,
    eventLoading,
    eventAssignmentsLoading,
    eventFormData,
    setEventFormData,
    eventLoadingError
  } = useEditEventData(
    eventIdParsed,
    isUserATL,
    isUserATLActingAsATM,
    setIsUserActingAsATM,
    setSelectedDate
  );

  const {
    data: orgUnitsData,
    loading: orgUnitsLoading,
    error: orgUnitsError
  } = useQuery(GET_SELECTED_ORG_FOR_EVENT, {
    variables: {
      eventId: eventIdParsed
    }
  });

  const isCustomerAsATM = location.state
    ? location.state.selectedAtmUser === currentUserInfo.Id
    : null;

  const pageMode = useCalendarPageMode(isUserActingAsATM ?? isCustomerAsATM, event || null);

  if (!eventLoading && event === null) {
    return <Navigate to={getDataNotFoundPage(currentUserTier)} />;
  }

  if (eventLoadingError || orgUnitsError) {
    return <ApolloError />;
  }

  return (
    <EventContextProvider
      pageMode={pageMode}
      eventStage={event?.Stage}
      eventType={event?.Type}
      isAutoCreated={event?.IsAutoCreated}
      eventFlags={event?.EventFlags}
      sowContractTypes={event?.SowContractTypes}
      atmRoleOptions={event?.AtmRoleOptions}
      eventId={eventIdParsed}
      assignmentsLoading={eventAssignmentsLoading}>
      <EditEventComponentWrapper
        eventIdParsed={eventIdParsed}
        pageMode={pageMode}
        event={event}
        eventLoading={eventLoading}
        eventFormData={eventFormData}
        setEventFormData={setEventFormData}
        orgUnitsData={orgUnitsData}
        orgUnitsLoading={orgUnitsLoading}
      />
    </EventContextProvider>
  );
};

export default EditEventPage;
